import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { FaceView } from './faceLayout';
import { DIM_OPACITY, PIN_SIZE, PIN_SIZE_SELECTED } from './markerStyle';
import {
  createPeopleLayer,
  DASHED,
  type MapPerson,
  type PeopleLayer,
} from './peopleLayer';

export type { MapPerson } from './peopleLayer';

export interface MapPin {
  id: string;
  latitude: number;
  longitude: number;
  /**
   * The number in the pin — a stop's order through the day, not the job number. Null draws
   * an unnumbered pin (the crew map's pick: a stop that is not in the picked tech's day must
   * not carry a second, competing number).
   */
  order: number | null;
  title: string;
  body: string;
  tone: 'scheduled' | 'active' | 'done' | 'problem';
  /** Faded: the page is talking about something else (the crew map's picked tech). */
  dimmed?: boolean;
}

/**
 * The map itself, and nothing else.
 *
 * Leaflet with OpenStreetMap tiles: no API key reaches the browser. Google is used for the
 * geocoding, server-side, on the key SONAR already has — and a Google Maps JavaScript map
 * would need a SECOND, publicly-visible key, which is a thing to create, restrict, rotate
 * and be billed per page view for. The tiles are not what makes a dispatch map useful; the
 * pins are.
 *
 * Leaflet is imperative and owns its own DOM, so React must not re-render into it. The map
 * is created once and the markers are swapped in a layer group — recreating the map on every
 * data change would reset the dispatcher's pan and zoom every time a visit moved, which is
 * the single most irritating thing a map can do.
 *
 * ⭐ Ticket 112 re-asked this question and got the same answer, so the paragraph above is a
 * live decision rather than history. 112's brief specified Google Maps JS; costing it out
 * found a second public key to create, restrict and rotate, per-page-view billing, AND a
 * Dockerfile + CI change, because `VITE_*` is inlined when the IMAGE builds and this
 * Dockerfile takes no build args — so a key set on the VPS would be `undefined` in the
 * shipped bundle. Meanwhile `GOOGLE_ROUTES_API_KEY` is unset in production, so every
 * polyline it would have drawn is null anyway. This component is keyless, unbilled and
 * already works, so 112 serves the fleet page from it and grew two optional props
 * (`base`, `showOrderPath`) rather than forking a second map.
 */
const TONE_COLOR: Record<MapPin['tone'], string> = {
  scheduled: '#2563eb',
  active: '#d97706',
  done: '#16a34a',
  problem: '#dc2626',
};

/**
 * A numbered dot, drawn as markup rather than shipped as four PNGs.
 *
 * ⚠️ The SELECTED pin is bigger and ringed, and it is deliberately not "a different colour".
 * Colour is already spoken for — it carries the visit's status — so recolouring the
 * selection would make a chosen scheduled stop look like a completed one. Size and ring are
 * the two channels still free.
 */
function pinIcon(pin: MapPin, selected: boolean): L.DivIcon {
  const size = selected ? PIN_SIZE_SELECTED : PIN_SIZE;
  return L.divIcon({
    className: '',
    // The accessible name rides INSIDE the icon: Leaflet applies `alt` only to an `<img>`
    // icon, so on this div it names nothing. In the markup, a new name is a new icon, which
    // `setIcon` writes into the SAME element (a focused pin keeps its focus).
    html: `<span class="sr-only">${escapeHtml(pinLabel(pin, selected))}</span><span aria-hidden="true" style="
      display:flex;align-items:center;justify-content:center;
      width:${size}px;height:${size}px;border-radius:999px;
      background:${TONE_COLOR[pin.tone]};color:#fff;
      font:600 ${selected ? 14 : 12}px/1 system-ui,sans-serif;
      box-shadow:${selected ? '0 0 0 4px rgb(0 0 0 / .25), 0 2px 6px rgb(0 0 0 / .5)' : '0 1px 4px rgb(0 0 0 / .4)'};
      border:${selected ? 3 : 2}px solid #fff;
      opacity:${pin.dimmed ? DIM_OPACITY : 1};
    ">${pin.order ?? ''}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const escapeHtml = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );

/**
 * A pin's accessible name. The selection is in it, not only in the pixels: a screen reader
 * user gets the same "this is the one" the ring gives everyone else.
 */
const pinLabel = (pin: MapPin, selected: boolean) =>
  `${pin.order === null ? 'Stop' : `Stop ${pin.order}`}: ${pin.title}${selected ? ' (selected)' : ''}`;

/**
 * The fit's key: each pin's id AND where it is, to about 10 cm (6 dp). A corrected address
 * moves its marker, and the frame must follow it, or the pin can sit outside the view. Its
 * selection, dimming, title and number are not in it: none of those moves anything.
 */
export function fitKey(
  pins: readonly MapPin[],
  base: MapBase | null | undefined,
): string {
  return [
    ...pins.map((p) => `${p.id}@${p.latitude.toFixed(6)},${p.longitude.toFixed(6)}`),
    base ? `b:${base.latitude},${base.longitude}` : '',
  ].join('|');
}

/** The map's box less the floating UI's insets, in the container's pixels. */
function visibleArea(size: L.Point, pad: MapInsets | null): FaceView {
  return {
    left: pad?.left ?? 0,
    top: pad?.top ?? 0,
    right: size.x - (pad?.right ?? 0),
    bottom: size.y - (pad?.bottom ?? 0),
  };
}

/** The margin a popup keeps from the floating UI, besides Leaflet's own 5 px. */
const POPUP_GAP = 8;

/**
 * A pin's popup pans the map, when it opens, clear of what floats over it: the SAME covered
 * insets the fit uses, so a popup never opens under a card, the chips or the panel. Leaflet
 * reads these options each time a popup opens, so a new padding needs no new popup.
 */
function padPopup(marker: L.Marker, pad: MapInsets | null): void {
  const popup = marker.getPopup();
  if (!popup) return;
  popup.options.autoPanPaddingTopLeft = [
    (pad?.left ?? 0) + POPUP_GAP,
    (pad?.top ?? 0) + POPUP_GAP,
  ];
  popup.options.autoPanPaddingBottomRight = [
    (pad?.right ?? 0) + POPUP_GAP,
    (pad?.bottom ?? 0) + POPUP_GAP,
  ];
}

/** Where a van's day starts and ends. Drawn differently from a stop, because it is one. */
export interface MapBase {
  latitude: number;
  longitude: number;
  label: string;
}

/**
 * Pixels along each edge of the map that floating UI covers, so a fit keeps every pin in the
 * part a reader can see.
 */
export interface MapInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function DispatchMap({
  pins,
  onSelect,
  selectedId,
  base,
  showOrderPath,
  fitPadding,
  people,
  onSelectPerson,
  onBackgroundClick,
  inert,
  className,
}: {
  pins: MapPin[];
  onSelect?: (id: string) => void;
  /**
   * The pin the rest of the page is currently talking about, drawn ringed and larger.
   *
   * ⭐ 113/A5's rule: a pin and its card must AGREE, or the two panes are two apps sharing
   * a screen. The map does not own the selection — the page does — so this is a prop and
   * never internal state; the same value highlights the board card and the list row.
   *
   * ⚠️ It does NOT pan or zoom to the pin. A dispatcher clicking a card on the board has
   * not asked the map to move, and a map that jumps on every selection loses the frame they
   * chose — the same complaint the create-once/swap-markers design exists to prevent.
   */
  selectedId?: string | null;
  /** The yard, if the vehicle has a geocoded one. Included in the fit. */
  base?: MapBase | null;
  /**
   * Join the pins in order with a DASHED line.
   *
   * ⚠️ Dashed, and never solid, because this is not a road. It is the ORDER of the day
   * drawn as a line — straight hops between stops, no street geometry, no traffic. A
   * solid line is what every mapping product uses for a real route, so drawing one here
   * would claim a driving path this deployment has not bought and cannot compute. The
   * map's own label says the same thing in words for anyone not reading dashes.
   */
  showOrderPath?: boolean;
  /**
   * What the page floats over the map (the crew map's header and lists), so the fit frames
   * the pins in the uncovered rest instead of under a card.
   *
   * ⚠️ NOT in the refit key. The cards change height for reasons the reader did not cause (a
   * "didn't refresh" notice, one more job not on the map), and a refit then throws away the
   * pan and zoom they chose. So every fit READS the current padding, and the padding itself
   * refits only once: the first measured value, which is the one the opening fit lacked.
   */
  fitPadding?: MapInsets | null;
  /**
   * The crew map's people: faces, and the dashed legs of whoever is en route.
   *
   * ⚠️ NOT in the refit key and NOT in the bounds. Every face sits at a job pin or on the leg
   * between two, so the pins' bounds already hold them; and people change on every refresh,
   * which must not throw away the reader's pan and zoom. They are drawn in a layer of their
   * own (`peopleLayer.ts`), so a people change never touches the pins either.
   */
  people?: readonly MapPerson[] | null;
  /** A face was clicked, or pressed with Enter or Space: the page picks that person. */
  onSelectPerson?: (id: string) => void;
  /**
   * A click on the map itself, on no pin and no face (every marker here is made with
   * `bubblingMouseEvents: false`, so its click never reaches the map): the page lets go of
   * its selection. A dashed leg is not a control, and a click on it counts as the map's.
   */
  onBackgroundClick?: () => void;
  /**
   * Take the whole map out of the keyboard and the pointer (`inert`): the crew map's phone
   * sheet, expanded, lies over the markers, and a Tab must not walk into what it hides.
   */
  inert?: boolean;
  className?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  /** The pins' own layer: base, order path and pins, each kept and updated by key. */
  const layer = useRef<L.LayerGroup | null>(null);
  const pinMarkers = useRef(new Map<string, { marker: L.Marker; shown: string }>());
  const baseMarker = useRef<{ marker: L.Marker; key: string } | null>(null);
  const orderPath = useRef<L.Polyline | null>(null);
  const peopleLayer = useRef<PeopleLayer | null>(null);
  /** The page's current callback, read by each marker's handler (markers outlive renders). */
  const select = useRef(onSelect);
  select.current = onSelect;
  const background = useRef(onBackgroundClick);
  background.current = onBackgroundClick;
  /** Refit only when the set of stops changes or one moves, not when one is re-iconed. */
  const fittedKey = useRef<string>('');
  /**
   * The current fit, callable from outside the effect that built it.
   *
   * 🔴 Leaflet measures its container ONCE, when the map is created, and every zoom
   * calculation after that is against the size it remembers. In a tab panel — and behind
   * Vite's JS-injected CSS — that first measurement can happen while the box is still
   * 0×0, and `fitBounds` against a 0×0 viewport silently resolves to the minimum zoom.
   * MEASURED: the fleet Routes map opened on the whole United States with one pin in
   * Ohio, tile URL `/4/3/6.png`. Nothing errored; the map simply showed a continent.
   */
  const refit = useRef<() => void>(() => {});
  /** The padding every fit reads, current at the moment it runs (see `fitPadding`). */
  const padding = useRef<MapInsets | null>(fitPadding ?? null);
  padding.current = fitPadding ?? null;
  /** Whether a fit has run WITH a measured padding yet. */
  const paddedFit = useRef(false);

  useEffect(() => {
    if (!host.current || map.current) return;
    const m = L.map(host.current, {
      // The default centre is the middle of the US, so an empty map reads as "no stops"
      // rather than as a bug about the Atlantic.
      center: [39.5, -98.35],
      zoom: 4,
      scrollWheelZoom: true,
    });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    const people = createPeopleLayer(m, () => visibleArea(m.getSize(), padding.current));
    peopleLayer.current = people;
    /*
     * Faces meet or part, and near or leave the floating cards, as the map moves: lay them out
     * again after every pan or zoom (`moveend` follows both) and resize, once per frame.
     */
    /*
     * ⚠️ The queued frame is CANCELLED on unmount, and a frame that runs anyway does nothing:
     * a relayout after `m.remove()` projects points on a map that has no panes left.
     */
    let frame: number | null = null;
    let gone = false;
    const relayout = () => {
      if (frame !== null || gone) return;
      const run = () => {
        frame = null;
        if (!gone) people.relayout();
      };
      if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(run);
      else run();
    };
    m.on('moveend resize', relayout);
    m.on('click', () => background.current?.());
    map.current = m;
    const pinsNow = pinMarkers.current;
    return () => {
      gone = true;
      if (frame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame);
      }
      frame = null;
      m.remove();
      map.current = null;
      layer.current = null;
      peopleLayer.current = null;
      pinsNow.clear();
      baseMarker.current = null;
      orderPath.current = null;
    };
  }, []);

  /**
   * The pins, kept by id: a pin that is still there is moved, re-iconed and re-stacked in
   * place, never rebuilt, so an open popup survives a selection, a pick and a refresh.
   */
  useEffect(() => {
    const m = map.current;
    const group = layer.current;
    if (!m || !group) return;

    const baseKey = base ? `${base.latitude},${base.longitude},${base.label}` : '';
    if (baseMarker.current && baseMarker.current.key !== baseKey) {
      group.removeLayer(baseMarker.current.marker);
      baseMarker.current = null;
    }
    if (base && !baseMarker.current) {
      const marker = L.marker([base.latitude, base.longitude], {
        icon: L.divIcon({
          className: '',
          // A SQUARE, not a numbered circle: the yard is not stop zero, and a reader
          // counting pins must not find an extra one in the sequence.
          html: `<span style="
            display:flex;align-items:center;justify-content:center;
            width:22px;height:22px;border-radius:4px;
            background:#111;color:#fff;
            font:600 11px/1 system-ui,sans-serif;
            box-shadow:0 1px 4px rgb(0 0 0 / .4);
            border:2px solid #fff;
          ">S</span>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
        title: base.label,
        keyboard: true,
        bubblingMouseEvents: false,
        alt: `Home base: ${base.label}`,
      })
        .bindPopup(`<strong>${escapeHtml(base.label)}</strong>`)
        .addTo(group);
      padPopup(marker, padding.current);
      baseMarker.current = { marker, key: baseKey };
    }

    const path =
      showOrderPath && pins.length > 1
        ? pins.map((p) => [p.latitude, p.longitude] as [number, number])
        : null;
    if (path && orderPath.current) orderPath.current.setLatLngs(path);
    else if (path) orderPath.current = L.polyline(path, DASHED).addTo(group);
    else if (orderPath.current) {
      group.removeLayer(orderPath.current);
      orderPath.current = null;
    }

    const kept = pinMarkers.current;
    const ids = new Set(pins.map((p) => p.id));
    for (const [id, k] of kept) {
      if (ids.has(id)) continue;
      group.removeLayer(k.marker);
      kept.delete(id);
    }
    for (const pin of pins) {
      const isSelected = pin.id === selectedId;
      const icon = pinIcon(pin, isSelected);
      const shown = String(icon.options.html);
      const popup = `<strong>${escapeHtml(pin.title)}</strong><br>${escapeHtml(pin.body)}`;
      // Above its neighbours, so the ring is never half-covered by the next pin along.
      const z = isSelected ? 1000 : 0;
      const k = kept.get(pin.id);
      if (k) {
        const at = L.latLng(pin.latitude, pin.longitude);
        if (!k.marker.getLatLng().equals(at)) k.marker.setLatLng(at);
        /*
         * ⚠️ Leaflet reads `title` (the tooltip) off its options only when it builds the
         * element, so a kept marker's tooltip is written on its element here. The name is in
         * the icon's markup (`pinIcon`), so `setIcon` already brought that up to date.
         */
        if (k.marker.options.title !== pin.title) {
          k.marker.options.title = pin.title;
          k.marker.getElement()?.setAttribute('title', pin.title);
        }
        if (k.shown !== shown) {
          k.shown = shown;
          k.marker.setIcon(icon);
        }
        k.marker.setZIndexOffset(z);
        k.marker.setPopupContent(popup);
        padPopup(k.marker, padding.current);
        continue;
      }
      const marker = L.marker([pin.latitude, pin.longitude], {
        icon,
        title: pin.title,
        keyboard: true,
        zIndexOffset: z,
        // A pin's click is the pin's: it must never reach the map as a background click,
        // which lets go of the selection the pin just made (`onBackgroundClick`). Leaflet
        // 1.9's marker default, said here so a default or a plugin cannot undo it.
        bubblingMouseEvents: false,
      }).bindPopup(popup);
      padPopup(marker, padding.current);
      const id = pin.id;
      marker.on('click', () => select.current?.(id));
      // Leaflet never turns a key into a click on a div-icon marker: Enter or Space opens it.
      marker.on('keydown', (e: L.LeafletKeyboardEvent) => {
        const key = e.originalEvent.key;
        if (key !== 'Enter' && key !== ' ') return;
        e.originalEvent.preventDefault();
        marker.openPopup();
        select.current?.(id);
      });
      marker.addTo(group);
      kept.set(id, { marker, shown });
    }

    // The base joins the KEY as well as the bounds — switching to a van with the same
    // stops but a different yard must refit, and a key built from pins alone would not. A
    // pin that MOVED refits too (`fitKey`).
    const key = fitKey(pins, base);
    const fit = () => {
      const points: [number, number][] = pins.map((p) => [p.latitude, p.longitude]);
      if (base) points.push([base.latitude, base.longitude]);
      if (points.length === 0) return;
      const bounds = L.latLngBounds(points);
      // A single stop has zero-area bounds, which fitBounds zooms to street level and looks
      // broken; pad it into a neighbourhood instead.
      const pad = padding.current;
      if (pad) paddedFit.current = true;
      m.fitBounds(bounds.pad(0.2), {
        maxZoom: points.length === 1 ? 14 : 16,
        ...(pad
          ? {
              paddingTopLeft: [pad.left, pad.top],
              paddingBottomRight: [pad.right, pad.bottom],
            }
          : {}),
      });
    };
    // Handed to the resize watcher below, which is the only thing that knows when the
    // container's real size finally exists.
    refit.current = fit;

    if (pins.length > 0 || base) {
      if (key !== fittedKey.current) {
        fittedKey.current = key;
        fit();
      }
    }
    // ⚠️ `selectedId` re-runs this effect (two pins are re-iconed) but is deliberately absent
    // from `key`, so a selection change never refits. Including it would make clicking a card
    // re-frame the whole map around one stop.
  }, [pins, selectedId, base, showOrderPath]);

  /** The people, in their own layer: drawing them never touches a pin. */
  useEffect(() => {
    peopleLayer.current?.draw(people ?? [], pins, onSelectPerson);
  }, [people, pins, onSelectPerson]);

  /**
   * New padding: the faces turn away from (or back towards) what the cards now cover, and a
   * popup opened from now on pans clear of them.
   */
  useEffect(() => {
    peopleLayer.current?.relayout();
    for (const { marker } of pinMarkers.current.values())
      padPopup(marker, fitPadding ?? null);
    if (baseMarker.current) padPopup(baseMarker.current.marker, fitPadding ?? null);
  }, [fitPadding]);

  /**
   * The padding's one refit: the first time a measured padding exists and no fit has used
   * one yet (the opening fit ran before the cards were measured). Later changes are only
   * READ by the next fit, never a reason for one.
   */
  useEffect(() => {
    if (fitPadding && !paddedFit.current && fittedKey.current !== '') refit.current();
  }, [fitPadding]);

  /**
   * Re-measure and re-fit whenever the container's box changes.
   *
   * `ResizeObserver` fires once on `observe()`, so this also covers the case the comment on
   * `refit` describes: the map was built against a 0×0 box and needs telling the truth as
   * soon as there is one. `invalidateSize()` alone is not enough — it corrects the viewport
   * but leaves the zoom that was computed from the wrong one, so the fit runs again after.
   *
   * A fit does not resize the container, so this cannot feed itself.
   */
  useEffect(() => {
    const el = host.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const m = map.current;
      if (!m) return;
      m.invalidateSize();
      refit.current();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={host}
      /**
       * 🔴 `isolate` is LOAD-BEARING, not a tidy-up.
       *
       * Leaflet stacks its own panes with absolute z-indexes — tiles 200, markers 600,
       * popups 700, controls 800 — and the container it sets `position: relative` on has
       * `z-index: auto`, so it creates NO stacking context. Those 600s and 800s therefore
       * compete directly with the rest of the page, and the page's overlays sit at z-50.
       *
       * MEASURED on `/schedule`: the job preview panel opened over the map column and the
       * map's markers and the OpenStreetMap attribution drew ON TOP of it — the attribution
       * landing squarely across the panel's "Open job" button, which is the primary action.
       * `isolation: isolate` gives the map its own stacking context, so Leaflet's internal
       * layering is preserved inside the box and the whole box sits in normal flow beneath
       * anything the app deliberately raises.
       *
       * ⚠️ Found by a SCREENSHOT, not by the geometry probe. Every box was in the right
       * place; only the paint order was wrong, and `getBoundingClientRect` cannot see that.
       */
      style={{ isolation: 'isolate' }}
      className={
        className ??
        'h-[60vh] min-h-80 w-full rounded-[var(--radius)] border border-border'
      }
      inert={inert || undefined}
      role="application"
      aria-label={
        showOrderPath
          ? "Map of this vehicle's stops. The dashed line shows the order of the day, not the roads driven."
          : "Map of the day's stops"
      }
    />
  );
}
